import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import { type DirectionFindInterface, DirectionFindDto } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import { type FilialFindInterface, FilialFindDto } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import { type UnitFindInterface, UnitFindDto } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import { type CompanyFindInterface, CompanyFindDto } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import { type ContragentFindInterface, ContragentFindDto } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import { type NdsFindInterface, NdsFindDto } from '@zaoblako/shared/modules/finance/dtos/nds-find.dto';
import { type UserGroupFindInterface, UserGroupFindDto } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { type TaskApproverFindInterface, TaskApproverFindDto } from '@zaoblako/shared/modules/task/dtos/auditors-and-approvers/task.approver.find.dto';
import { type TaskOrderFindInterface, TaskOrderFindDto } from '@zaoblako/shared/modules/task/dtos/order/task-order-find.dto';
import { type TaskStatusFindInterface, TaskStatusFindDto } from '@zaoblako/shared/modules/task/dtos/status/task-status-find.dto';
import { type TaskPriorityFindInterface, TaskPriorityFindDto } from '@zaoblako/shared/modules/task/dtos/task-priority-find.dto';
import { type ApprovalSchemeFindInterface, ApprovalSchemeFindDto } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-find.dto';
import { type UserFindInterface, UserFindDto } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { ThreadFindInterface } from '@zaoblako/shared/modules/messenger/dtos/thread/thread-find.dto';
import { type BankDetailsFindInterface, BankDetailsFindDto } from '@zaoblako/shared/modules/client/dtos/bank.details/bank-details-find.dto';
import type { TaskTemplateFindInterface } from '@zaoblako/shared/modules/task/dtos/template/task-template-find.dto';
import { CostCenterFindDto, type CostCenterFindInterface } from '@zaoblako/shared/modules/catalog/dtos/cost-center/cost-center-find.dto';
import { CurrencyFindDto, type CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import { TaskPaymentTypeEnum } from '@zaoblako/shared/modules/task/enum/task.payment.type.enum';
import { TaskDtoKeysEnum } from '@zaoblako/shared/modules/task/enum/task-dto-keys.enum';
import { MessengerDtoKeysEnum } from '@zaoblako/shared/modules/messenger/enums/messenger-dto-keys.enum';
import { BusinessDtoKeysEnum } from '@zaoblako/shared/modules/business/enum/business-dto-keys.enum';
import { TaskKindEnum } from '@zaoblako/shared/modules/task/enum/task-kind.enum';
import {
  BudgetClassificationCodeFindDto,
  type BudgetClassificationCodeFindInterface,
} from '@zaoblako/shared/modules/catalog/dtos/budget-classification-code/budget-classification-code-find.dto';
import { booleanSchema } from '@zaoblako/shared/modules/serde/schemas/boolean.schema';
import { PayerStatusFindDto, type PayerStatusFindInterface } from '@zaoblako/shared/modules//catalog/dtos/payer-status/payer-status-find.dto';

import * as yup from 'yup';

export interface TaskFindInterface {
  id: number;
  created: Date;
  date: Date;
  subject: string;
  amount: number;
  vat: number;
  type: TaskPaymentTypeEnum;
  kind?: TaskKindEnum;
  ref: string;
  factAmount: number;
  createdBy?: UserFindInterface;
  responsible?: UserFindInterface;
  /** Approval scheme to use */
  scheme: ApprovalSchemeFindInterface;
  contragent: ContragentFindInterface;
  bankDetails: BankDetailsFindInterface;
  organization: CompanyFindInterface;
  unit: UnitFindInterface;
  filial: FilialFindInterface;
  direction: DirectionFindInterface;
  priority: TaskPriorityFindInterface;
  status: TaskStatusFindInterface;
  currency: CurrencyFindInterface;
  nds: NdsFindInterface;
  orders: TaskOrderFindInterface[];
  filesCount: number;
  userAuditors: UserFindInterface[];
  userGroupAuditors: UserGroupFindInterface[];
  approvers: TaskApproverFindInterface[];
  expired?: boolean;
  canEdit?: boolean;
  canApprove?: boolean;
  rate: number;
  thread?: ThreadFindInterface;
  deal?: DealFindInterface;
  costCenter?: CostCenterFindInterface;
  template?: TaskTemplateFindInterface;
  /** Budget classification code, using only when {@link TaskFindInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  budgetClassificactionCode?: BudgetClassificationCodeFindInterface;
  /** Municipality classification code (rus. "ОКТМО"), using only when {@link TaskFindInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  municipalityClassificationCode?: string;
  /** Payer status, using only when {@link TaskFindInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  payerStatus?: PayerStatusFindInterface;
  /** Unique Accrual Identifier (rus. "УИН"), using only when {@link TaskFindInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  uniqueAccrualIdentifier?: string;
  /** Payment reason, using only when {@link TaskFindInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  paymentReason?: string;
  /** Tax period, using only when {@link TaskFindInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  taxPeriod?: string;
  /** Document number, using only when {@link TaskFindInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  documentNumber?: string;
  /** Document date, using only when {@link TaskFindInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  documentDate?: Date;
  /** Payment by credit funds */
  creditPayment: boolean;
}

export const TaskFindDto = SerdeDtoClass.create<TaskFindInterface>(
  TaskDtoKeysEnum.TASK_FIND_DTO,
  {
    id: {
      sortable: true,
      label: 'Номер',
      serializer: yup.number(),
    },
    created: {
      label: 'Создан',
      serializer: yup.date(),
    },
    date: {
      sortable: true,
      label: 'Дата',
      serializer: yup.date(),
    },
    subject: {
      sortable: true,
      label: 'Назначение платежа',
      serializer: yup.string(),
    },
    amount: {
      sortable: true,
      label: 'Сумма',
      serializer: yup.number(),
    },
    vat: {
      label: 'Сумма НДС',
      serializer: yup.number(),
    },
    type: {
      sortable: true,
      label: 'Тип платежа',
      serializer: yup
        .string()
        .oneOf(Object.values(TaskPaymentTypeEnum)),
    },
    kind: {
      sortable: true,
      label: 'Вид платежа',
      serializer: yup
        .string()
        .oneOf(Object.values(TaskKindEnum)),
    },
    ref: {
      label: 'Ссылка',
      serializer: yup.string(),
    },
    factAmount: {
      sortable: true,
      label: 'Фактическая сумма',
      serializer: yup.number(),
    },
    createdBy: {
      sortable: true,
      optional: true,
      label: 'Автор',
      nested: UserFindDto,
      serializer: (_, nested) => nested,
    },
    responsible: {
      sortable: true,
      optional: true,
      label: 'Ответственный',
      nested: UserFindDto,
      serializer: (_, nested) => nested,
    },
    scheme: {
      label: 'Схема согласования',
      nested: ApprovalSchemeFindDto,
      serializer: (_, nested) => nested,
    },
    contragent: {
      sortable: true,
      label: 'Контрагент',
      nested: ContragentFindDto,
      serializer: (_, nested) => nested,
    },
    bankDetails: {
      label: 'Рассчетный счет',
      nested: BankDetailsFindDto,
      serializer: (_, nested) => nested,
    },
    organization: {
      sortable: true,
      label: 'Плательщик',
      nested: CompanyFindDto,
      serializer: (_, nested) => nested,
    },
    unit: {
      sortable: true,
      label: 'БЕ',
      nested: UnitFindDto,
      serializer: (_, nested) => nested,
    },
    filial: {
      sortable: true,
      label: 'Филиал',
      nested: FilialFindDto,
      serializer: (_, nested) => nested,
    },
    direction: {
      sortable: true,
      label: 'БС',
      nested: DirectionFindDto,
      serializer: (_, nested) => nested,
    },
    priority: {
      sortable: true,
      label: 'Приоритет',
      nested: TaskPriorityFindDto,
      serializer: (_, nested) => nested,
    },
    status: {
      sortable: true,
      label: 'Статус',
      nested: TaskStatusFindDto,
      serializer: (_, nested) => nested,
    },
    currency: {
      sortable: true,
      label: 'Валюта',
      nested: CurrencyFindDto,
      serializer: (_, nested) => nested,
    },
    nds: {
      label: 'Ставка НДС',
      nested: NdsFindDto,
      serializer: (_, nested) => nested,
    },
    orders: {
      label: 'Заявки на платеж',
      nested: TaskOrderFindDto,
      serializer: (_, nested) => yup.array(nested),
    },
    filesCount: {
      label: 'Количество файлов',
      serializer: yup.number(),
    },
    userAuditors: {
      label: 'Аудиторы',
      nested: UserFindDto,
      serializer: (_, nested) => yup.array(nested),
    },
    userGroupAuditors: {
      label: 'Группы аудиторов',
      nested: UserGroupFindDto,
      serializer: (_, nested) => yup.array(nested),
    },
    approvers: {
      label: 'Согласователи',
      nested: TaskApproverFindDto,
      serializer: (_, nested) => yup.array(nested),
    },
    expired: {
      optional: true,
      label: 'Просрочена',
      serializer: yup.boolean(),
    },
    canEdit: {
      optional: true,
      label: 'Текущий пользователь может редактировать заявку',
      serializer: yup.boolean(),
    },
    canApprove: {
      optional: true,
      label: 'Текущий пользователь может согласовывать заявку',
      serializer: yup.boolean(),
    },
    rate: {
      label: 'Курс выбранной валюты на дату заявки',
      serializer: yup.number(),
    },
    thread: {
      optional: true,
      label: 'Чат заявки (комментарии)',
      nested: MessengerDtoKeysEnum.THREAD_FIND_DTO,
      serializer: (_, nested) => nested,
    },
    deal: {
      label: 'Сделка',
      nested: BusinessDtoKeysEnum.DEAL_FIND_DTO,
      serializer: (_, nested) => nested,
    },
    costCenter: {
      sortable: true,
      optional: true,
      label: 'Центр затрат',
      nested: CostCenterFindDto,
      serializer: (_, nested) => nested,
    },
    template: {
      optional: true,
      label: 'Шаблон',
      nested: TaskDtoKeysEnum.TASK_TEMPLATE_FIND_DTO,
      serializer: (_, nested) => nested,
    },
    budgetClassificactionCode: {
      optional: true,
      label: 'КБК',
      nested: BudgetClassificationCodeFindDto,
      serializer: (_, nested) => nested,
    },
    municipalityClassificationCode: {
      optional: true,
      label: 'ОКТМО',
      serializer: yup.string(),
    },
    payerStatus: {
      optional: true,
      label: 'Статус плательщика',
      nested: PayerStatusFindDto,
      serializer: (_, nested) => nested,
    },
    uniqueAccrualIdentifier: {
      optional: true,
      label: 'УИН',
      serializer: yup.string(),
    },
    paymentReason: {
      optional: true,
      label: 'Основание платежа',
      serializer: yup.string(),
    },
    taxPeriod: {
      optional: true,
      label: 'Налоговый период',
      serializer: yup.string(),
    },
    documentNumber: {
      optional: true,
      label: 'Номер документа',
      serializer: yup.string(),
    },
    documentDate: {
      optional: true,
      label: 'Дата документа',
      serializer: yup.date(),
    },
    creditPayment: {
      optional: true,
      label: 'Оплата через кредитные средства',
      serializer: booleanSchema,
    },
  },
);
